import { useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { buildAuthHref, rememberAuthIntent } from '@/features/auth/auth-intent';
import { useAuthStore } from '@/features/auth/store';
import { InstallEducationPrompt } from '@/features/pwa-install/InstallEducationPrompt';
import { PullToRefreshProvider } from '@/pwa/PullToRefreshProvider';
import { YandexMetricaPageViewTracker } from '@/shared/components/YandexMetricaPageViewTracker';

export type MiniappShellFrame = 'mobile-frame' | 'adaptive-app';

interface MiniappShellProps {
  frame?: MiniappShellFrame;
}

const frameClass: Record<MiniappShellFrame, string> = {
  'mobile-frame': '',
  'adaptive-app':
    'min-[768px]:max-w-[var(--desktop-app-max-width)] min-[768px]:px-[var(--desktop-gutter)]',
};

function getRouteFrame(pathname: string): MiniappShellFrame {
  return pathname === '/catalog' ||
    pathname === '/profile' ||
    pathname === '/auth' ||
    pathname === '/legal' ||
    pathname.startsWith('/legal/') ||
    pathname === '/install' ||
    pathname.startsWith('/install/') ||
    pathname.startsWith('/clubs/') ||
    pathname.startsWith('/paywall/')
    ? 'adaptive-app'
    : 'mobile-frame';
}

export function MiniappShell({ frame }: MiniappShellProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const initialPathnameRef = useRef(location.pathname);
  const resolvedFrame = frame ?? getRouteFrame(location.pathname);
  const runtime = useAuthStore((s) => s.runtime);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isReady = useAuthStore((s) => s.isReady);
  const showSignedOutBanner =
    isReady &&
    runtime === 'web' &&
    !isAuthenticated &&
    location.pathname !== '/auth' &&
    !location.pathname.startsWith('/legal') &&
    !location.pathname.startsWith('/install');
  const returnTo = `${location.pathname}${location.search}${location.hash}`;

  function handleOpenAuth() {
    rememberAuthIntent(returnTo);
    void navigate(buildAuthHref(returnTo));
  }

  return (
    <div className="min-h-[var(--app-height)] bg-[var(--app-canvas)] text-[var(--foreground)]">
      <div
        className={[
          'relative mx-auto flex w-full max-w-[var(--shell-frame-max-width)] flex-col px-[var(--shell-inline-pad)]',
          frameClass[resolvedFrame],
        ].join(' ')}
        data-miniapp-shell-frame={resolvedFrame}
        style={{ minHeight: 'var(--app-height, 100dvh)' }}
      >
        <PullToRefreshProvider pathname={location.pathname}>
          <div
            className="relative flex min-h-[var(--app-height)] flex-1 flex-col bg-[var(--shell-surface)] min-[430px]:border-x min-[430px]:border-[rgba(26,26,26,0.06)]"
            data-miniapp-shell-surface=""
            style={{ minHeight: 'var(--app-height, 100dvh)' }}
          >
            <YandexMetricaPageViewTracker />
            {showSignedOutBanner && (
              <div className="px-[var(--page-gutter)] pt-[calc(var(--safe-area-top)+0.375rem)]">
                <div className="rounded-[20px] border border-[rgba(248,152,104,0.26)] bg-[linear-gradient(180deg,rgba(255,248,236,0.96)_0%,rgba(255,250,242,0.92)_100%)] px-3.5 py-3 shadow-[0_8px_18px_rgba(217,156,108,0.08)]">
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-black tracking-[-0.01em] text-[var(--foreground)]">
                        Доступ к книгам
                      </p>
                      <p className="text-[12px] leading-4 text-[rgba(26,26,26,0.62)]">
                        Сохраняйте прогресс чтения
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleOpenAuth}
                      className="inline-flex h-9 shrink-0 items-center justify-center rounded-full bg-[var(--primary)] px-4 text-[11px] font-extrabold uppercase tracking-[0.04em] text-[var(--primary-foreground)] shadow-[0_10px_22px_rgba(107,78,230,0.22)] transition-all active:scale-[0.98]"
                    >
                      Войти
                    </button>
                  </div>
                </div>
              </div>
            )}
            <InstallEducationPrompt initialPathname={initialPathnameRef.current} />
            <div className="relative flex min-h-[var(--app-height)] flex-1 flex-col">
              <Outlet />
            </div>
          </div>
        </PullToRefreshProvider>
      </div>
    </div>
  );
}
